import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, ShoppingBag, Factory, Landmark, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const PERSONAS = [
    {
        id: "bank",
        label: "Bank / NBFC",
        icon: Building2,
        headline: "Portfolio-level climate exposure, without core integration.",
        detail: "Financed-emissions attribution, sector heatmaps, and hidden concentration surfaced — read-only, supervisor-grade, and auditable to PCAF v2.",
        outputs: ["Portfolio Climate Risk dashboard", "Financed Risk Score (FRS™)", "Counterparty drill-down", "RBI-aligned dossier"],
        cta: { label: "Bank workflow", to: "/what-we-do" }
    },
    {
        id: "buyer",
        label: "Large Buyer / OEM",
        icon: ShoppingBag,
        headline: "Supplier-level Scope 3 visibility across the value chain.",
        detail: "Priority Scope 3 mapping, supplier-risk heatmaps, and delisting-cycle early-warning — so procurement can price transition risk before the KPI hits.",
        outputs: ["Supplier Scope 3 map", "Value chain drill-down", "Delisting-cycle alerts", "CBAM buyer dossier"],
        cta: { label: "Buyer workflow", to: "/what-we-do" }
    },
    {
        id: "msme",
        label: "Exporting MSME",
        icon: Factory,
        headline: "Know your CBAM exposure before your buyer or lender does.",
        detail: "Fast baseline inventory, a portable Carbonis Score for buyers and banks, and a ranked abatement roadmap sized for MSME cash-flow reality.",
        outputs: ["CBAM exposure dossier", "Carbonis Score (portable)", "Abatement roadmap", "Bank-shareable report"],
        cta: { label: "MSME workflow", to: "/what-we-do" }
    },
    {
        id: "regulator",
        label: "Regulator",
        icon: Landmark,
        headline: "Systemic climate risk visibility, sector by sector.",
        detail: "Aggregated systemic view, sector-level risk maps, policy sandbox and stress testing — methodology-versioned and audit-trailed.",
        outputs: ["Systemic risk heatmap", "Sector benchmarks", "Policy simulation", "Audit trail"],
        cta: { label: "Regulator workflow", to: "/what-we-do" }
    }
];

export default function PersonaPicker() {
    const [active, setActive] = useState("bank");
    const current = PERSONAS.find((p) => p.id === active);

    return (
        <section
            className="relative py-24 lg:py-32 border-t border-navy/10"
            data-testid="section-persona-picker"
        >
            <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-14">
                    <div className="lg:col-span-6">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-px bg-gold"></div>
                            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">
                                Choose Your Vantage
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em] text-navy">
                            I am a… <em className="text-gold not-italic font-light">start here.</em>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 lg:col-start-8 lg:self-end">
                        <p className="text-navy/60 text-lg leading-relaxed">
                            Same intelligence layer, four workflows. Pick your seat and the site shows you what Carbonis produces for it.
                        </p>
                    </div>
                </div>

                {/* Persona buttons */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-navy/10 border border-navy/10 mb-8">
                    {PERSONAS.map((p) => {
                        const Icon = p.icon;
                        const isActive = p.id === active;
                        return (
                            <button
                                key={p.id}
                                onClick={() => setActive(p.id)}
                                data-testid={`persona-btn-${p.id}`}
                                className={`p-5 lg:p-6 flex items-center gap-3 transition-colors duration-300 ${
                                    isActive ? "bg-navy text-paper" : "bg-paper text-navy hover:bg-white"
                                }`}
                            >
                                <Icon size={18} strokeWidth={1.35} className={isActive ? "text-gold" : "text-gold/80"} />
                                <span className="font-mono text-[11px] lg:text-[12px] tracking-[0.16em] uppercase text-left leading-tight">
                                    {p.label}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Active persona panel */}
                <div className="border border-navy/10 bg-paper min-h-[280px]">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={current.id}
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 p-8 lg:p-12"
                            data-testid={`persona-panel-${current.id}`}
                        >
                            <div className="lg:col-span-7">
                                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold mb-3">
                                    {current.label}
                                </div>
                                <h3 className="font-serif text-3xl lg:text-4xl leading-[1.1] tracking-[-0.01em] text-navy mb-5">
                                    {current.headline}
                                </h3>
                                <p className="text-navy/60 text-base lg:text-lg leading-relaxed max-w-[560px]">
                                    {current.detail}
                                </p>
                                <Link
                                    to={current.cta.to}
                                    className="btn-institution mt-8 inline-flex"
                                    data-testid={`persona-cta-${current.id}`}
                                >
                                    {current.cta.label}
                                    <ArrowRight size={14} strokeWidth={1.5} />
                                </Link>
                            </div>
                            <div className="lg:col-span-5 lg:border-l lg:border-navy/10 lg:pl-10">
                                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-navy/50 mb-4">
                                    What you get
                                </div>
                                <ul className="flex flex-col gap-3">
                                    {current.outputs.map((o) => (
                                        <li key={o} className="flex items-start gap-3">
                                            <span className="text-gold mt-2 w-2 h-px bg-gold"></span>
                                            <span className="text-navy text-[15px]">{o}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
}
